import {
  useRef,
  useState,
  DragEvent,
  useEffect,
  ReactElement,
  useCallback,
} from 'react';
import { Trash2, X, RefreshCw } from 'react-feather';
import UploadIcon from '../../../assets/svg/Upload.svg';
import FilePDFIcon from '../../../assets/svg/FilePDF.svg';
import FileJPEGIcon from '../../../assets/svg/FileJPEG.svg';
import FileJPGIcon from '../../../assets/svg/FileJPG.svg';
import FilePNGIcon from '../../../assets/svg/FilePNG.svg';
import FileDOCXIcon from '../../../assets/svg/FileDOCX.svg';
import { Button } from '../Buttons';
import { ButtonVariant } from '../Buttons/types';
import { Tooltip } from '../Tooltip';
import { Loader } from '../Loader';
import { FILE_UPLOAD_LABELS, FileType, FILE_TYPE_CONFIG } from './constants';
import { isValidFilename } from '../../utils/fileValidation';
import {
  getFileTypeFromSignature,
  validateFileSignature,
} from '../../utils/fileSignatureValidation';
import { SHARED_FILE_ERRORS } from '../../constants/fileErrors';

interface FileUploadProps {
  onFileSelect: (files: File[]) => void;
  selectedFiles?: File[];
  existingFiles?: Array<{ filename: string; fileSize: number; path: string }>;
  onExistingFileDelete?: (path: string) => void;
  allowedTypes: FileType[];
  maxFiles?: number;
  onError?: (message: string) => void;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string | undefined;
  uploadProgress?: Record<string, number> | undefined;
  failedFiles?: string[] | undefined;
  onRetry?: ((filename: string) => void) | undefined;
  hideMaxFilesMessage?: boolean;
  fullWidth?: boolean;
}

export default function FileUpload({
  onFileSelect,
  selectedFiles = [],
  existingFiles = [],
  onExistingFileDelete,
  allowedTypes,
  maxFiles = 1,
  onError,
  disabled = false,
  error = false,
  errorMessage,
  uploadProgress,
  failedFiles,
  onRetry,
  hideMaxFilesMessage = false,
  fullWidth = false,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [fileIcons, setFileIcons] = useState<{ [key: string]: ReactElement }>(
    {}
  );
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const [displayedExistingFiles, setDisplayedExistingFiles] =
    useState(existingFiles);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevExistingFilesLengthRef = useRef(existingFiles.length);

  useEffect(() => {
    if (existingFiles.length !== prevExistingFilesLengthRef.current) {
      setDisplayedExistingFiles(existingFiles);
      prevExistingFilesLengthRef.current = existingFiles.length;
    }
  }, [existingFiles]);

  const acceptString = allowedTypes
    .map((type) => FILE_TYPE_CONFIG[type].mimeType)
    .join(',');

  const getIconFromFilename = (filename: string): ReactElement => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <FilePDFIcon />;
      case 'jpeg':
        return <FileJPEGIcon />;
      case 'jpg':
        return <FileJPGIcon />;
      case 'png':
        return <FilePNGIcon />;
      case 'docx':
        return <FileDOCXIcon />;
      default:
        return <FilePDFIcon />;
    }
  };

  const handleDeleteExistingFile = (index: number) => {
    const fileToDelete = displayedExistingFiles[index];
    if (fileToDelete && onExistingFileDelete) {
      onExistingFileDelete(fileToDelete.path);
    }
    setDisplayedExistingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const droppedFiles = Array.from(e.dataTransfer.files);

    // Check for invalid filenames first
    const invalidFile = droppedFiles.find(
      (file) => !isValidFilename(file.name)
    );
    if (invalidFile) {
      setHasError(true);
      setTimeout(() => setHasError(false), 3000);
      if (onError) {
        onError(SHARED_FILE_ERRORS.INVALID_FILENAME);
      }
      return;
    }

    // Check for duplicate files
    const existingFileNames = new Set([
      ...selectedFiles.map((f) => f.name),
      ...displayedExistingFiles.map((f) => f.filename),
    ]);
    const duplicateFile = droppedFiles.find((file) =>
      existingFileNames.has(file.name)
    );
    if (duplicateFile) {
      setHasError(true);
      setTimeout(() => setHasError(false), 3000);
      if (onError) {
        onError(SHARED_FILE_ERRORS.DUPLICATE_FILE);
      }
      return;
    }

    let hasSizeError = false;
    let hasCorruptionError = false;

    const validFiles = [];

    for (const file of droppedFiles) {
      const fileType = allowedTypes.find(
        (type) => FILE_TYPE_CONFIG[type].mimeType === file.type
      );
      if (!fileType) {
        continue;
      }

      const maxSize = FILE_TYPE_CONFIG[fileType].maxSize * 1024 * 1024;
      if (file.size > maxSize) {
        hasSizeError = true;
        continue;
      }

      // Validate file signature
      const allowedMimeTypes = allowedTypes.map(
        (type) => FILE_TYPE_CONFIG[type].mimeType
      );
      const isValidSignature = await validateFileSignature(
        file,
        allowedMimeTypes
      );
      if (!isValidSignature) {
        hasCorruptionError = true;
        continue;
      }

      validFiles.push(file);
    }

    // Show error if any files are invalid
    if (validFiles.length < droppedFiles.length) {
      setHasError(true);
      setTimeout(() => setHasError(false), 3000);
      if (onError) {
        if (hasCorruptionError) {
          onError(SHARED_FILE_ERRORS.FILE_CORRUPTED);
        } else if (hasSizeError) {
          onError(SHARED_FILE_ERRORS.FILE_SIZE);
        } else {
          onError(SHARED_FILE_ERRORS.FILE_FORMAT);
        }
      }
    }

    const availableSlots =
      maxFiles - selectedFiles.length - displayedExistingFiles.length;
    const newFiles = validFiles.slice(0, Math.max(0, availableSlots));

    if (newFiles.length > 0) {
      onFileSelect([...selectedFiles, ...newFiles]);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFilesList = e.target.files ? Array.from(e.target.files) : [];

    // Check for invalid filenames first
    const invalidFile = selectedFilesList.find(
      (file) => !isValidFilename(file.name)
    );
    if (invalidFile) {
      setHasError(true);
      setTimeout(() => setHasError(false), 3000);
      if (onError) {
        onError(SHARED_FILE_ERRORS.INVALID_FILENAME);
      }
      // Reset file input to allow re-selection
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check for duplicate files
    const existingFileNames = new Set([
      ...selectedFiles.map((f) => f.name),
      ...displayedExistingFiles.map((f) => f.filename),
    ]);
    const duplicateFile = selectedFilesList.find((file) =>
      existingFileNames.has(file.name)
    );
    if (duplicateFile) {
      setHasError(true);
      setTimeout(() => setHasError(false), 3000);
      if (onError) {
        onError(SHARED_FILE_ERRORS.DUPLICATE_FILE);
      }
      // Reset file input to allow re-selection
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    let hasSizeError = false;
    let hasCorruptionError = false;

    const validFiles = [];

    for (const file of selectedFilesList) {
      const fileType = allowedTypes.find(
        (type) => FILE_TYPE_CONFIG[type].mimeType === file.type
      );
      if (!fileType) {
        continue;
      }

      const maxSize = FILE_TYPE_CONFIG[fileType].maxSize * 1024 * 1024;
      if (file.size > maxSize) {
        hasSizeError = true;
        continue;
      }

      // Validate file signature
      const allowedMimeTypes = allowedTypes.map(
        (type) => FILE_TYPE_CONFIG[type].mimeType
      );
      const isValidSignature = await validateFileSignature(
        file,
        allowedMimeTypes
      );
      if (!isValidSignature) {
        hasCorruptionError = true;
        continue;
      }

      validFiles.push(file);
    }

    // Show error if any files are invalid
    if (validFiles.length < selectedFilesList.length) {
      setHasError(true);
      setTimeout(() => setHasError(false), 3000);
      if (onError) {
        if (hasCorruptionError) {
          onError(SHARED_FILE_ERRORS.FILE_CORRUPTED);
        } else if (hasSizeError) {
          onError(SHARED_FILE_ERRORS.FILE_SIZE);
        } else {
          onError(SHARED_FILE_ERRORS.FILE_FORMAT);
        }
      }
      // Reset file input to allow re-selection
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }

    const availableSlots =
      maxFiles - selectedFiles.length - displayedExistingFiles.length;
    const newFiles = validFiles.slice(0, Math.max(0, availableSlots));

    if (newFiles.length > 0) {
      // Mark files as uploading
      const newFileNames = new Set(newFiles.map((f) => f.name));
      setUploadingFiles(newFileNames);

      // Simulate upload delay (remove loader after 1 second)
      setTimeout(() => {
        setUploadingFiles(new Set());
      }, 1000);

      onFileSelect([...selectedFiles, ...newFiles]);
    }

    // Reset file input after successful upload
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = (filename: string) => {
    const updated = selectedFiles.filter((f) => f.name !== filename);
    onFileSelect(updated);
  };

  const updateFileIcons = useCallback(async (files: File[]) => {
    const icons: { [key: string]: ReactElement } = {};
    for (const file of files) {
      const actualType = await getFileTypeFromSignature(file);
      switch (actualType) {
        case 'pdf':
          icons[file.name] = <FilePDFIcon />;
          break;
        case 'jpeg': {
          const extension = file.name.split('.').pop()?.toLowerCase();
          icons[file.name] =
            extension === 'jpeg' ? <FileJPEGIcon /> : <FileJPGIcon />;
          break;
        }
        case 'png':
          icons[file.name] = <FilePNGIcon />;
          break;
        case 'docx':
          icons[file.name] = <FileDOCXIcon />;
          break;
        default:
          icons[file.name] = <FilePDFIcon />;
      }
    }
    setFileIcons(icons);
  }, []);

  useEffect(() => {
    if (selectedFiles.length > 0) {
      updateFileIcons(selectedFiles);
    }
  }, [selectedFiles, updateFileIcons]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="flex flex-col gap-2">
      {!hideMaxFilesMessage && (
        <p className="text-[14px] leading-[20px] tracking-[0.25px] text-start text-[#474A4C] font-normal">
          {maxFiles === 1
            ? FILE_UPLOAD_LABELS.MAX_FILES_MESSAGE_SINGULAR
            : FILE_UPLOAD_LABELS.MAX_FILES_MESSAGE_PLURAL.replace(
                '{count}',
                maxFiles.toString()
              )}
        </p>
      )}

      {selectedFiles.filter((f) => !(failedFiles?.includes(f.name) ?? false))
        .length +
        displayedExistingFiles.length <
        maxFiles && (
        <div
          onDragEnter={disabled ? undefined : handleDragEnter}
          onDragOver={disabled ? undefined : handleDragOver}
          onDragLeave={disabled ? undefined : handleDragLeave}
          onDrop={disabled ? undefined : handleDrop}
          onClick={disabled ? undefined : () => fileInputRef.current?.click()}
          className={`
            ${fullWidth ? 'w-full' : 'w-full sm:w-[500px] md:w-[600px] lg:w-[652px]'}
            h-[180px] sm:h-[200px] md:h-[220px] lg:h-[228px]
            border-2 border-dashed rounded-3xl
            flex flex-col items-center justify-center gap-2
            transition-colors px-2
            ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            ${
              isDragging
                ? 'border-[#2A4455] bg-[#E8EEF0]'
                : hasError || error
                  ? 'border-[#e11b1b] bg-white'
                  : 'border-gray-300 bg-white hover:bg-gray-100'
            }
          `}
        >
          <div style={{ width: '24px', height: '24px' }}>
            <UploadIcon />
          </div>
          <div className="text-center mt-6">
            <p className="text-[14px] leading-[20px] tracking-[0.25px] text-center text-[#2A4455] font-normal">
              {maxFiles > 1
                ? FILE_UPLOAD_LABELS.DRAG_DROP_PLURAL
                : FILE_UPLOAD_LABELS.DRAG_DROP_SINGULAR}
            </p>
            <p className="text-[12px] leading-[16px] tracking-[0.4px] text-center text-[#878B8E] font-normal mt-2">
              {FILE_UPLOAD_LABELS.SUPPORTED_FORMATS_PREFIX}{' '}
              {allowedTypes.map((type, index) => (
                <span
                  key={type}
                  className="text-[12px] leading-[16px] tracking-[0.4px] text-[#2A4455] font-normal"
                >
                  {type}
                  {index < allowedTypes.length - 1 && ', '}
                </span>
              ))}
            </p>
            <p className="text-[12px] leading-[16px] tracking-[0.4px] text-center text-[#878B8E] font-normal">
              {FILE_UPLOAD_LABELS.MAX_FILE_SIZE}{' '}
              <span className="text-[12px] leading-[16px] tracking-[0.4px] text-[#2A4455] font-normal">
                {Math.max(
                  ...allowedTypes.map((type) => FILE_TYPE_CONFIG[type].maxSize)
                )}
                MB
              </span>
            </p>
            <div className="mt-6">
              <Button
                label={FILE_UPLOAD_LABELS.BROWSE_FILES}
                variant={ButtonVariant.Secondary}
                onClick={(e) => {
                  e?.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={disabled}
                customBgColor="#FFFFFF"
                customBgColorHover="#474A4C14"
                customBorderColor="#DCDDE0"
                customTextColor="#474A4C"
                customFontWeight={500}
              />
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptString}
            multiple={maxFiles > 1}
            onChange={handleFileChange}
            disabled={disabled}
            className="hidden"
          />
        </div>
      )}

      {errorMessage && (
        <p className="text-bodySmall text-error-40 pl-4">{errorMessage}</p>
      )}

      {(displayedExistingFiles.length > 0 || selectedFiles.length > 0) && (
        <div
          className={`flex flex-col gap-2 ${fullWidth ? 'w-full' : 'w-full sm:w-[500px] md:w-[600px] lg:w-[652px]'}`}
        >
          {/* Existing Files */}
          {displayedExistingFiles.map((file, index) => (
            <div
              key={`existing-${index}`}
              className={`flex items-center justify-between p-4 sm:p-3 bg-white rounded ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center gap-3 sm:gap-2 flex-1 min-w-0">
                <div
                  style={{ width: '40px', height: '40px' }}
                  className="sm:w-[32px] sm:h-[32px] flex-shrink-0"
                >
                  {getIconFromFilename(file.filename)}
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <Tooltip
                    content={file.filename}
                    align="center"
                    contentMaxWidth={500}
                    useContentWidth={true}
                  >
                    <p className="text-[16px] sm:text-[14px] leading-[24px] sm:leading-[20px] tracking-[0.5px] text-[#2A4455] font-normal truncate pr-2">
                      {file.filename}
                    </p>
                  </Tooltip>
                  <p className="text-[12px] sm:text-[11px] leading-[16px] sm:leading-[14px] tracking-[0.4px] text-[#878B8E] font-normal">
                    {file.fileSize < 1024
                      ? `${file.fileSize} B`
                      : file.fileSize < 1024 * 1024
                        ? `${(file.fileSize / 1024).toFixed(1)} KB`
                        : `${(file.fileSize / 1024 / 1024).toFixed(1)} MB`}
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Tooltip
                  content={FILE_UPLOAD_LABELS.REMOVE_FILE}
                  contentMinWidth={80}
                  align="center"
                  forceShow={true}
                  triggerWrapContent={true}
                >
                  <button
                    type="button"
                    onClick={() => handleDeleteExistingFile(index)}
                    disabled={disabled}
                    className={`rounded focus:outline-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`file-upload-delete-existing-${index}`}
                    tabIndex={-1}
                  >
                    <Trash2
                      size={24}
                      className="sm:w-[20px] sm:h-[20px] text-[#E11B1B]"
                    />
                  </button>
                </Tooltip>
              </div>
            </div>
          ))}

          {/* New Files */}
          {[...selectedFiles].reverse().map((file, index) => {
            const isUploading = uploadProgress?.[file.name] !== undefined;
            const isFailed = failedFiles?.includes(file.name) ?? false;
            const isLegacyUploading =
              !isUploading && !isFailed && uploadingFiles.has(file.name);
            const progress = uploadProgress?.[file.name] ?? 0;

            return (
              <div
                key={index}
                className={`flex items-center justify-between gap-3 sm:gap-2 p-4 sm:p-3 bg-white rounded ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center gap-3 sm:gap-2 flex-1 min-w-0">
                  <div
                    style={{ width: '40px', height: '40px' }}
                    className="sm:w-[32px] sm:h-[32px] flex-shrink-0"
                  >
                    {fileIcons[file.name] || <FilePDFIcon />}
                  </div>
                  <div className="flex flex-col min-w-0 flex-1">
                    <Tooltip
                      content={file.name}
                      align="center"
                      contentMaxWidth={500}
                      useContentWidth={true}
                    >
                      <p className="text-[16px] sm:text-[14px] leading-[24px] sm:leading-[20px] tracking-[0.5px] text-[#2A4455] font-normal truncate pr-2">
                        {file.name}
                      </p>
                    </Tooltip>
                    {isUploading ? (
                      <>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1">
                          <div
                            className="h-1.5 bg-[#2A4455] rounded-full transition-all duration-300"
                            style={{
                              width: `${Math.min(100, (progress / file.size) * 100)}%`,
                            }}
                          />
                        </div>
                        <p className="text-[12px] leading-[16px] tracking-[0.4px] text-[#878B8E] font-normal mt-0.5">
                          {`(${formatFileSize(progress)} / ${formatFileSize(file.size)})`}
                        </p>
                      </>
                    ) : isFailed ? (
                      <p className="text-[12px] leading-[16px] tracking-[0.4px] text-[#e11b1b] font-normal">
                        {FILE_UPLOAD_LABELS.UPLOAD_ERROR}
                      </p>
                    ) : (
                      <p className="text-[12px] sm:text-[11px] leading-[16px] sm:leading-[14px] tracking-[0.4px] text-[#878B8E] font-normal">
                        {formatFileSize(file.size)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-shrink-0">
                  {isLegacyUploading ? (
                    <button
                      type="button"
                      disabled
                      className="p-1 rounded opacity-50 cursor-not-allowed"
                    >
                      <div
                        style={{ width: '24px', height: '24px' }}
                        className="sm:w-[20px] sm:h-[20px] flex items-center justify-center"
                      >
                        <Loader size={48} />
                      </div>
                    </button>
                  ) : isUploading || isFailed ? (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          lineHeight: 0,
                        }}
                      >
                        <Tooltip
                          content={
                            isUploading
                              ? FILE_UPLOAD_LABELS.CANCEL_UPLOAD
                              : FILE_UPLOAD_LABELS.REMOVE_FILE
                          }
                          contentMinWidth={120}
                          align="center"
                          forceShow={true}
                          triggerWrapContent={true}
                        >
                          <button
                            type="button"
                            onClick={() => handleDelete(file.name)}
                            disabled={disabled}
                            className={`rounded focus:outline-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            data-testid={`file-upload-delete-${index}`}
                            tabIndex={-1}
                          >
                            <X
                              size={24}
                              className="sm:w-[20px] sm:h-[20px] text-[#474A4C]"
                            />
                          </button>
                        </Tooltip>
                      </div>
                      {isFailed && onRetry && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: 0,
                          }}
                        >
                          <Tooltip
                            content={FILE_UPLOAD_LABELS.RETRY_UPLOAD}
                            contentMinWidth={120}
                            align="center"
                            forceShow={true}
                            triggerWrapContent={true}
                          >
                            <button
                              type="button"
                              onClick={() => onRetry(file.name)}
                              className="rounded focus:outline-none cursor-pointer"
                              data-testid={`file-upload-retry-${index}`}
                              tabIndex={-1}
                            >
                              <RefreshCw
                                size={24}
                                className="sm:w-[20px] sm:h-[20px] text-[#474A4C]"
                              />
                            </button>
                          </Tooltip>
                        </div>
                      )}
                    </>
                  ) : (
                    <Tooltip
                      content={FILE_UPLOAD_LABELS.REMOVE_FILE}
                      contentMinWidth={80}
                      align="center"
                      forceShow={true}
                      triggerWrapContent={true}
                    >
                      <button
                        type="button"
                        onClick={() => handleDelete(file.name)}
                        disabled={disabled}
                        className={`rounded focus:outline-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        data-testid={`file-upload-delete-${index}`}
                        tabIndex={-1}
                      >
                        <Trash2
                          size={24}
                          className="sm:w-[20px] sm:h-[20px] text-[#E11B1B]"
                        />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
